import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Card,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enIN } from 'date-fns/locale';
import { useDropzone } from 'react-dropzone';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { createAMC, updateAMC } from '../../store/amcSlice';
import amcService from '../../services/amcService';

const AMCForm = ({ amc, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.amc);
  
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      clientName: '',
      date: new Date(),
      isAMC: true,
      allCameraOK: false,
      dvrDatetimeOK: false,
      recording: false,
      remarks: '',
    },
  });

  const watchedDate = watch('date');

  useEffect(() => {
    if (amc) {
      reset({
        clientName: amc.clientName || '',
        date: new Date(amc.date) || new Date(),
        isAMC: amc.isAMC !== undefined ? amc.isAMC : true,
        allCameraOK: amc.allCameraOK || false,
        dvrDatetimeOK: amc.dvrDatetimeOK || false,
        recording: amc.recording || false,
        remarks: amc.remarks || '',
      });
      setImages(amc.images || []);
    }
  }, [amc, reset]);

  const onDrop = async (acceptedFiles) => {
    if (!amc?._id) {
      // For new AMC, just store files locally until AMC is created
      const newImages = acceptedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        isNew: true
      }));
      setImages(prev => [...prev, ...newImages]);
      return;
    }

    // For existing AMC, upload immediately
    setUploadingImages(true);
    try {
      const formData = new FormData();
      acceptedFiles.forEach(file => {
        formData.append('images', file);
      });

      const response = await amcService.uploadImages(amc._id, formData);
      setImages(prev => [...prev, ...response.data.images]);
    } catch (error) {
      console.error('Failed to upload images:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxFiles: 100 - images.length
  });

  const removeImage = async (index) => {
    const image = images[index];
    
    if (image.isNew) {
      // Remove local file
      const newImages = images.filter((_, i) => i !== index);
      setImages(newImages);
      return;
    }

    if (amc?._id && image._id) {
      // Remove from server
      try {
        await amcService.deleteImage(amc._id, image._id);
        setImages(prev => prev.filter((_, i) => i !== index));
      } catch (error) {
        console.error('Failed to delete image:', error);
      }
    }
  };

  const onSubmit = async (data) => {
    try {
      if (!data.date) {
        console.error('Date is required');
        return;
      }
      
      const formData = {
        ...data,
        date: data.date.toISOString(),
      };

      let result;
      if (amc) {
        result = await dispatch(updateAMC({ id: amc._id, data: formData })).unwrap();
      } else {
        result = await dispatch(createAMC(formData)).unwrap();
        
        // Upload images for new AMC
        if (images.length > 0 && result.amc._id) {
          const uploadFormData = new FormData();
          images.forEach(img => {
            if (img.file) {
              uploadFormData.append('images', img.file);
            }
          });
          await amcService.uploadImages(result.amc._id, uploadFormData);
        }
      }

      onSuccess(amc ? 'AMC updated successfully' : 'AMC created successfully');
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              {...register('clientName', { required: 'Client name is required' })}
              fullWidth
              label="College/Client Name"
              error={!!errors.clientName}
              helperText={errors.clientName?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Date"
              value={watchedDate}
              onChange={(newValue) => setValue('date', newValue, { shouldValidate: true })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  ...register('date', { required: 'Date is required' }),
                  error: !!errors.date,
                  helperText: errors.date?.message
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  {...register('isAMC')}
                  defaultChecked={true}
                />
              }
              label="AMC (if unchecked, regular service)"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      {...register('allCameraOK')}
                      defaultChecked={false}
                    />
                  }
                  label="All Cameras OK"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      {...register('dvrDatetimeOK')}
                      defaultChecked={false}
                    />
                  }
                  label="DVR DateTime OK"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      {...register('recording')}
                      defaultChecked={false}
                    />
                  }
                  label="Recording Active"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register('remarks')}
              fullWidth
              label="Remarks"
              multiline
              rows={3}
              placeholder="Enter any remarks or notes..."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Images (up to 100 compressed images)
            </Typography>
            
            {images.length < 100 && (
              <Card sx={{ mb: 2 }}>
                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    bgcolor: isDragActive ? 'primary.50' : 'grey.50',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input {...getInputProps()} />
                  <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    {isDragActive ? 'Drop images here' : 'Drag & drop images or click to select'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supported formats: JPEG, PNG, GIF (Max 5MB each, up to 100 images)
                  </Typography>
                  {uploadingImages && (
                    <Box sx={{ mt: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Box>
              </Card>
            )}

            {images.length > 0 && (
              <ImageList cols={3} gap={8}>
                {images.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image.preview || `http://localhost:5000/${image.path}`}
                      alt={`AMC ${index + 1}`}
                      loading="lazy"
                      style={{ height: 200, objectFit: 'cover' }}
                    />
                    <ImageListItemBar
                      title={image.originalName || `Image ${index + 1}`}
                      actionIcon={
                        <IconButton
                          sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                          onClick={() => removeImage(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {amc ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AMCForm;